/**
 * API service layer for notifications
 *
 * Routes calls to either the real Beacon API or the local mock layer
 * based on the VITE_USE_MOCK_NOTIFICATIONS environment variable.
 *
 * Toggle:
 *   VITE_USE_MOCK_NOTIFICATIONS=true  → mock data (default for local dev)
 *   VITE_USE_MOCK_NOTIFICATIONS=false → live Beacon Notification Service
 */

import type {
  NotificationsApiResponse,
  FetchNotificationsParams,
  FetchUnreadCountParams,
  UnreadCountResponse,
  NotificationSettingsResponse,
  UpdateNotificationSettingsPayload,
} from './types';
import { generateMockNotifications, updateMockNotification } from './mockData';
import { normalizeNotificationSettings } from './settings/utils';
import {
  fetchBeaconNotifications,
  fetchBeaconUnreadCount,
  markBeaconNotificationRead,
  markBeaconNotificationUnread,
  starBeaconNotification,
  unstarBeaconNotification,
  fetchBeaconDigestNotifications,
} from '../../services/beaconApi';
import {
  fetchNotificationSettings as fetchControlPanelNotificationSettings,
  updateNotificationSettings as updateControlPanelNotificationSettings,
} from '../../services/entityApi';

const USE_MOCK = import.meta.env.VITE_USE_MOCK_NOTIFICATIONS === 'true';

// ---------------------------------------------------------------------------
// Mock implementations (unchanged logic, adapted to new types)
// ---------------------------------------------------------------------------

/**
 * Mock notification settings data stored in memory
 */
let mockSettingsData: NotificationSettingsResponse = {
  defaults: normalizeNotificationSettings({
    informational: { inApp: true, email: false },
    actionable: { inApp: true, email: true },
  }),
  overrides: [],
};

async function fetchNotificationsMock(
  params?: FetchNotificationsParams
): Promise<NotificationsApiResponse> {
  // TODO: Replace with real API call when backend is ready
  // const queryParams = new URLSearchParams();
  // if (params?.limit) queryParams.append('limit', String(params.limit));
  // if (params?.unreadOnly) queryParams.append('unreadOnly', String(params.unreadOnly));
  // if (params?.cursor) queryParams.append('cursor', params.cursor);
  //
  // const url = `/api/notifications/v1/notifications?${queryParams}`;
  // return authFetch<NotificationsApiResponse>(url, token);

  // Mock implementation: Simulate API delay and return mock data
  await new Promise((resolve) => setTimeout(resolve, 2500));

  const allNotifications = generateMockNotifications();

  // Apply read filter if specified (currently supported by real API)
  let filteredNotifications =
    params?.read === 'false'
      ? allNotifications.filter((n) => !n.read)
      : params?.read === 'true'
        ? allNotifications.filter((n) => n.read)
        : allNotifications;

  // Apply starred filter if specified
  if (params?.starred === 'true') {
    filteredNotifications = filteredNotifications.filter((n) => n.starred);
  } else if (params?.starred === 'false') {
    filteredNotifications = filteredNotifications.filter((n) => !n.starred);
  }

  // Apply actionable filter (only "true" is effective, matches real API behavior)
  if (params?.actionable === 'true') {
    filteredNotifications = filteredNotifications.filter((n) => n.taskId != null);
  }

  // Apply subject URN prefix filter
  if (params?.subject) {
    const prefix = params.subject;
    filteredNotifications = filteredNotifications.filter(
      (n) => n.subject?.startsWith(prefix) ?? false
    );
  }

  // Parse cursor to extract offset (format: "cursor_{offset}")
  const offset = params?.cursor ? parseInt(params.cursor.replace('cursor_', ''), 10) : 0;
  const limit = params?.limit ?? 20;
  const items = filteredNotifications.slice(offset, offset + limit);

  const nextOffset = offset + limit;
  const hasMore = nextOffset < filteredNotifications.length;
  const nextCursor = hasMore ? `cursor_${nextOffset}` : undefined;

  return { items, nextCursor, hasMore };
}

async function fetchUnreadCountMock(params?: FetchUnreadCountParams): Promise<UnreadCountResponse> {
  await new Promise((resolve) => setTimeout(resolve, 200));
  let notifications = generateMockNotifications();

  // Default to unread-only (matches real API)
  if (params?.read !== 'true') {
    notifications = notifications.filter((n) => !n.read);
  }
  if (params?.actionable === 'true') {
    notifications = notifications.filter((n) => n.taskId != null);
  }
  if (params?.subject) {
    const prefix = params.subject;
    notifications = notifications.filter((n) => n.subject?.startsWith(prefix) ?? false);
  }

  return {
    entityId: 'mock',
    unreadOnly: true,
    starredOnly: false,
    actionableOnly: params?.actionable === 'true',
    subjectPrefix: params?.subject,
    count: notifications.length,
  };
}

async function markNotificationReadMock(notificationId: string): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 300));
  updateMockNotification(notificationId, { read: true, readAt: new Date().toISOString() });
}

async function markNotificationUnreadMock(notificationId: string): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 300));
  updateMockNotification(notificationId, { read: false, readAt: undefined });
}

async function starNotificationMock(notificationId: string): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 300));
  updateMockNotification(notificationId, { starred: true });
}

async function unstarNotificationMock(notificationId: string): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 300));
  updateMockNotification(notificationId, { starred: false });
}

async function fetchNotificationSettingsMock(): Promise<NotificationSettingsResponse> {
  await new Promise((resolve) => setTimeout(resolve, 300));
  // MOCK-ONLY: Deep clone to simulate API behavior and prevent test pollution.
  // Do NOT use this pattern in real API code - TanStack Query handles structural sharing.
  return JSON.parse(JSON.stringify(mockSettingsData));
}

async function updateNotificationSettingsMock(
  payload: UpdateNotificationSettingsPayload
): Promise<NotificationSettingsResponse> {
  await new Promise((resolve) => setTimeout(resolve, 400));
  // Update the mock data store
  mockSettingsData = { ...payload };
  // MOCK-ONLY: Deep clone to simulate API behavior.
  // Do NOT use this pattern in real API code - TanStack Query handles structural sharing.
  return JSON.parse(JSON.stringify(mockSettingsData));
}

// ---------------------------------------------------------------------------
// Public API (delegates to mock or real Beacon backend)
// ---------------------------------------------------------------------------

/**
 * Fetches notifications for the current user/entity.
 * Supports cursor-based pagination and optional filters.
 */
export async function fetchNotifications(
  token: string,
  entityId: string,
  params?: FetchNotificationsParams,
  signal?: AbortSignal
): Promise<NotificationsApiResponse> {
  if (USE_MOCK) {
    return fetchNotificationsMock(params);
  }
  return fetchBeaconNotifications(token, entityId, params, signal);
}

/**
 * Fetches the server-side unread notification count for an entity.
 * Supports optional filtering by actionable status and/or subject URN prefix.
 */
export async function fetchUnreadCount(
  token: string,
  entityId: string,
  params?: FetchUnreadCountParams,
  signal?: AbortSignal
): Promise<UnreadCountResponse> {
  if (USE_MOCK) {
    return fetchUnreadCountMock(params);
  }
  return fetchBeaconUnreadCount(token, entityId, params, signal);
}

/**
 * Marks a notification as read.
 */
export async function markNotificationRead(
  token: string,
  entityId: string,
  notificationId: string
): Promise<void> {
  if (USE_MOCK) {
    return markNotificationReadMock(notificationId);
  }
  return markBeaconNotificationRead(token, entityId, notificationId);
}

/**
 * Marks a notification as unread.
 */
export async function markNotificationUnread(
  token: string,
  entityId: string,
  notificationId: string
): Promise<void> {
  if (USE_MOCK) {
    return markNotificationUnreadMock(notificationId);
  }
  return markBeaconNotificationUnread(token, entityId, notificationId);
}

/**
 * Updates the read status of a single notification.
 * Convenience wrapper that delegates to markNotificationRead or markNotificationUnread.
 */
export async function updateNotificationReadStatus(
  token: string,
  entityId: string,
  notificationId: string,
  read: boolean
): Promise<void> {
  if (read) {
    return markNotificationRead(token, entityId, notificationId);
  }
  return markNotificationUnread(token, entityId, notificationId);
}

/**
 * Stars a notification (save for later).
 */
export async function starNotification(
  token: string,
  entityId: string,
  notificationId: string
): Promise<void> {
  if (USE_MOCK) {
    return starNotificationMock(notificationId);
  }
  return starBeaconNotification(token, entityId, notificationId);
}

/**
 * Unstars a notification (remove from saved).
 */
export async function unstarNotification(
  token: string,
  entityId: string,
  notificationId: string
): Promise<void> {
  if (USE_MOCK) {
    return unstarNotificationMock(notificationId);
  }
  return unstarBeaconNotification(token, entityId, notificationId);
}

/**
 * Updates the starred status of a single notification.
 * Convenience wrapper that delegates to starNotification or unstarNotification.
 */
export async function updateNotificationStarredStatus(
  token: string,
  entityId: string,
  notificationId: string,
  starred: boolean
): Promise<void> {
  if (starred) {
    return starNotification(token, entityId, notificationId);
  }
  return unstarNotification(token, entityId, notificationId);
}

/**
 * Batch update read status for multiple notifications.
 * Note: Not supported by MVP backend API yet — falls back to sequential calls.
 */
export async function bulkUpdateReadStatus(
  notificationIds: string[],
  read: boolean,
  token: string,
  entityId: string
): Promise<void> {
  await Promise.all(
    notificationIds.map((id) => updateNotificationReadStatus(token, entityId, id, read))
  );
}

/**
 * Fetches notifications belonging to a specific digest window.
 * GET /api/v1/notifications/digest/{digestId}?entityId=…
 * When mock is enabled, returns the first page of mock notifications (no server digest window).
 */
export async function fetchDigestNotifications(
  token: string,
  digestId: string,
  entityId: string,
  signal?: AbortSignal
): Promise<NotificationsApiResponse> {
  if (USE_MOCK) {
    // Mock: return a small slice of notifications to simulate the digest window
    const allNotifications = generateMockNotifications();
    return { items: allNotifications.slice(0, 5), hasMore: false };
  }
  return fetchBeaconDigestNotifications(token, digestId, entityId, signal);
}

/**
 * Fetches notification settings for the current user.
 * Returns defaults and entity-specific overrides.
 * Uses Control Panel API: /api/access/user/v1/{userUuid}/settings/notification-settings
 */
export async function fetchNotificationSettings(
  token: string,
  userUuid: string
): Promise<NotificationSettingsResponse> {
  if (USE_MOCK) {
    return fetchNotificationSettingsMock();
  }
  return fetchControlPanelNotificationSettings(userUuid, token);
}

/**
 * Updates notification settings for the current user.
 * Requires the complete settings payload (defaults + all overrides).
 * Uses Control Panel API: PUT /api/access/user/v1/{userUuid}/settings/notification-settings
 */
export async function updateNotificationSettings(
  token: string,
  userUuid: string,
  payload: UpdateNotificationSettingsPayload
): Promise<NotificationSettingsResponse> {
  if (USE_MOCK) {
    return updateNotificationSettingsMock(payload);
  }
  return updateControlPanelNotificationSettings(userUuid, token, payload);
}
